import { useNavigate, useParams, useSearchParams } from 'react-router'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { BatchCallsListView } from './batchCalls/BatchCallsListView'
import { BatchCallForm } from './batchCalls/BatchCallForm'
import { BatchCallDetailsView } from './batchCalls/BatchCallDetailsView'
import {
  useBatchCallDetail,
  useCancelBatchCall,
  useRetryFailedBatchCall,
} from '@/api/batchCalls'

export function BatchCallsView() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const [searchParams, setSearchParams] = useSearchParams()

  // Sous-vue stockée dans l'URL : ?bcView=create | edit | details + ?bcId=N
  const bcView = searchParams.get('bcView')
  const bcId = Number(searchParams.get('bcId') || '') || null

  // Récupère la campagne en édition/détails via TanStack Query.
  const { data: editingBatch, isLoading: loadingEdit } = useBatchCallDetail(
    bcView === 'edit' ? bcId : null,
  )

  const cancelBatch = useCancelBatchCall()
  const retryFailed = useRetryFailedBatchCall()

  const setBcView = (view: 'create' | 'edit' | 'details' | null, id?: number) => {
    const next = new URLSearchParams(searchParams)
    if (view === null) {
      next.delete('bcView')
      next.delete('bcId')
    } else {
      next.set('bcView', view)
      if (id != null) next.set('bcId', String(id))
      else next.delete('bcId')
    }
    setSearchParams(next)
  }

  const goToAIAgents = () => {
    navigate(`/${locale}/manager?view=ai-agents`)
  }

  const handleCancelFromDetails = async () => {
    if (!bcId) return
    try {
      await cancelBatch.mutateAsync(bcId)
      toast.success(t('manager.batchCalls.toast.cancelled'))
      setBcView(null)
    } catch {
      toast.error(t('manager.batchCalls.toast.cancelError'))
    }
  }

  const handleRetryFromDetails = async () => {
    if (!bcId) return
    try {
      await retryFailed.mutateAsync(bcId)
      toast.success(t('manager.batchCalls.toast.retryQueued'))
      setBcView(null)
    } catch {
      toast.error(t('manager.batchCalls.toast.retryError'))
    }
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="px-6 lg:px-8 py-6">
          {bcView === 'create' ? (
            <BatchCallForm
              initialData={null}
              onSuccess={() => setBcView(null)}
              onCancel={() => setBcView(null)}
              onNavigateToAIAgents={goToAIAgents}
            />
          ) : bcView === 'edit' && bcId ? (
            loadingEdit ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : (
              <BatchCallForm
                initialData={editingBatch ?? null}
                onSuccess={() => setBcView(null)}
                onCancel={() => setBcView(null)}
                onNavigateToAIAgents={goToAIAgents}
              />
            )
          ) : bcView === 'details' && bcId ? (
            <BatchCallDetailsView
              batchId={bcId}
              onBack={() => setBcView(null)}
              onCancel={() => void handleCancelFromDetails()}
              onRetry={() => void handleRetryFromDetails()}
            />
          ) : (
            <BatchCallsListView
              onCreate={() => setBcView('create')}
              onEdit={(batch) => setBcView('edit', batch.id)}
              onOpenDetails={(batch) => setBcView('details', batch.id)}
            />
          )}
        </div>
      </div>
    </div>
  )
}
